import React, { forwardRef } from "react";
import { Dumbbell, Users, Flame } from "lucide-react";
import { fitTitleWithWrap } from "../lib/fitText";

/**
 * 1080×1080 shareable squad-invite card. Renders the invite code in giant
 * tracked-out type and prints the live URL underneath so a recipient who
 * sees the image (no clickable link) can still type it in.
 */
const SquadShareCard = forwardRef(function SquadShareCard({ squadName, inviteCode, ownerName, url = "hypergains.fit" }, ref) {
    // Squad names are user-generated → could be anything from "IRON" (4 chars)
    // to "The Weekend Warriors Fitness Squad" (33 chars). Full-width column
    // (952px). Wrap to two lines only when single-line would drop below 70px.
    const title = fitTitleWithWrap(squadName || "Their Squad", {
        maxWidth: 952,
        maxSize: 130,
        minSize: 60,
        wrapThreshold: 70,
    });
    return (
        <div
            ref={ref}
            data-testid="squad-share-card"
            style={{
                width: 1080,
                height: 1080,
                background: "#0a0a0a",
                color: "#ffffff",
                position: "relative",
                overflow: "hidden",
                fontFamily: '"Barlow Condensed", "DM Sans", system-ui, sans-serif',
            }}
        >
            {/* Diagonal hatch background */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(255,59,48,0.06) 0px, rgba(255,59,48,0.06) 2px, transparent 2px, transparent 28px)",
                }}
            />
            {/* Red wash */}
            <div
                style={{
                    position: "absolute",
                    right: -260,
                    top: -260,
                    width: 800,
                    height: 800,
                    background: "radial-gradient(circle, rgba(255,59,48,0.35) 0%, rgba(255,59,48,0) 60%)",
                    filter: "blur(8px)",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: -200,
                    bottom: -240,
                    width: 800,
                    height: 800,
                    background: "radial-gradient(circle, rgba(255,59,48,0.22) 0%, rgba(255,59,48,0) 60%)",
                    filter: "blur(8px)",
                }}
            />

            {/* Brand bar */}
            <div style={{ position: "absolute", top: 60, left: 64, right: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Dumbbell size={36} color="#ff3b30" />
                    <span style={{ fontWeight: 900, fontSize: 38, letterSpacing: -0.5, textTransform: "uppercase" }}>
                        Hyper<span style={{ color: "#ff3b30" }}>gains</span>
                    </span>
                </div>
                <span style={{ fontSize: 16, letterSpacing: 5, textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
                    Squad Invite
                </span>
            </div>

            {/* Center */}
            <div style={{ position: "absolute", left: 64, right: 64, top: 220, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, color: "#ff3b30", fontSize: 22, letterSpacing: 6, textTransform: "uppercase", fontWeight: 800 }}>
                    <Users size={32} color="#ff3b30" /> You've been called up
                </div>
                <div style={{ marginTop: 22, fontSize: 36, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.8)" }}>
                    {ownerName ? `${ownerName} wants you in` : "You're invited to"}
                </div>
                <div style={{ marginTop: 14, fontWeight: 900, fontSize: title.size, lineHeight: 0.92, letterSpacing: -2, textTransform: "uppercase", color: "#ffffff" }}>
                    {title.lines.map((line, i) => (
                        <div key={i}>{line}</div>
                    ))}
                </div>
            </div>

            {/* Invite code block */}
            <div style={{ position: "absolute", left: 64, right: 64, top: 700, border: "3px solid #ff3b30", padding: "28px 36px", background: "rgba(255,59,48,0.10)" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 20 }}>
                    <span style={{ fontSize: 18, letterSpacing: 6, textTransform: "uppercase", color: "#ff3b30", fontWeight: 800 }}>
                        <Flame size={20} color="#ff3b30" style={{ verticalAlign: "-2px", marginRight: 8 }} /> Invite Code
                    </span>
                    <span style={{ fontSize: 18, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>
                        5 lifters max · weekly leaderboard
                    </span>
                </div>
                <div style={{ marginTop: 14, fontWeight: 900, fontSize: 110, letterSpacing: 18, lineHeight: 1, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>
                    {inviteCode || "------"}
                </div>
            </div>

            {/* URL footer (typeable) */}
            <div style={{ position: "absolute", left: 64, right: 64, bottom: 56, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ fontSize: 16, letterSpacing: 5, textTransform: "uppercase", color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>
                    Sign up · enter the code · grind together
                </div>
                <div style={{ marginTop: 8, fontSize: 36, letterSpacing: 2, color: "#ff3b30", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
                    {url}
                </div>
            </div>
        </div>
    );
});

export default SquadShareCard;
